import {
  AllSelectedFlags,
  CustomReportParameters,
  CustomReportWithTemplateParameters,
} from "@/mixins/ReportBuilder/ReportBuilderMixin";
import { AUDITEL_TARGET_REQUIRED_MESSAGE } from "./granularity";
import { isAuditelUnit } from "./reportUnit";
import {
  CompetitorReportTemplateParameters,
  FiltersListPermittedParams,
} from "@/mixins/http/ReportTemplateMixin";
import {
  ReportCompany,
  ReportSector,
} from "@/mixins/http/CustomReport/ReportProductMixin";
import { ReportTemplateCompetitor } from "@/models";
import MultiSelectBroadcaster, {
  Agency,
} from "@/models/MultiSelectBroadcaster";
import { coalesceArrayToNull } from "@/services/Utilities";
import { formatApiDate } from "@/services/ApiDateHelper";

const ALL_TOKEN: FiltersListPermittedParams = ["all"];

export type PendingSelectAll = {
  broadcasters: boolean;
  agencies: boolean;
  productTypes: string[];
  sectorTypes: string[];
};

export const emptyPendingSelectAll = (): PendingSelectAll => ({
  broadcasters: false,
  agencies: false,
  productTypes: [],
  sectorTypes: [],
});

export function validateCompetitorAttributes(
  attrs: CompetitorReportTemplateParameters
): string | null {
  if (attrs.competitors != null && attrs.agencies != null) {
    return "Seleziona solo emittenti oppure solo concessionarie, non entrambe.";
  }
  const hasProductOptions =
    attrs.companies != null || attrs.brands != null || attrs.products != null;
  const hasSectorOptions =
    attrs.sectors != null || attrs.categories != null || attrs.classes != null;
  if (hasProductOptions && hasSectorOptions) {
    return "Seleziona solo aziende/brand/prodotti oppure solo settori/categorie/classi, non entrambi.";
  }
  if (isAuditelUnit(attrs.type) && attrs.auditel_target == null) {
    return AUDITEL_TARGET_REQUIRED_MESSAGE;
  }
  return null;
}

export function buildCompetitorAttributes(
  parameters: CustomReportParameters,
  allSelected: AllSelectedFlags
): CompetitorReportTemplateParameters {
  const p = parameters;
  const ids = (arr: Array<{ id?: number }> | undefined) =>
    coalesceArrayToNull(
      arr?.map((e) => e.id).filter((id): id is number => id != null)
    );
  const idsByType = (
    arr: Array<{ id?: number; type?: string }> | undefined,
    type: string
  ) => ids(arr?.filter((e) => e.type === type));

  const all = allSelected;
  return {
    competitors: all.broadcasters ? ALL_TOKEN : ids(p.broadcasters),
    agencies: all.agencies ? ALL_TOKEN : ids(p.agencies),
    companies: all.companies ? ALL_TOKEN : idsByType(p.products, "company"),
    brands: all.brands ? ALL_TOKEN : idsByType(p.products, "brand"),
    products: all.products ? ALL_TOKEN : idsByType(p.products, "product"),
    sectors: all.sectors ? ALL_TOKEN : idsByType(p.sectors, "sector"),
    categories: all.categories ? ALL_TOKEN : idsByType(p.sectors, "category"),
    classes: all.classes ? ALL_TOKEN : idsByType(p.sectors, "class"),
    type: p.unit,
    raw: p.output_type === "raw",
    format_detail: p.format_detail,
    temporal_detail: p.granularity_enabled,
    granularity: p.granularity_enabled ? p.granularity ?? null : null,
    week_type: "eu",
    quota: p.quotas,
    inclusion: p.inclusion,
    show_special_initiatives: p.special_initiatives_detail,
    show_vod_devices: p.show_vod_devices,
    apply_client_database: p.apply_client_database,
    attach_sector_category_class: p.attach_sector_category_class,
    auditel_target: isAuditelUnit(p.unit) ? p.auditel_target?.id ?? null : null,
  };
}

export function templateExportParameters(
  parameters: CustomReportParameters,
  templateId: string,
  granularity?: string | null
): CustomReportWithTemplateParameters {
  const params: CustomReportWithTemplateParameters = {
    template_id: templateId,
    starts_at: parameters.starts_at,
    ends_at: parameters.ends_at,
    compare: parameters.compare,
    compare_starts_at: parameters.compare
      ? parameters.compare_starts_at
      : undefined,
    compare_ends_at: parameters.compare
      ? parameters.compare_ends_at
      : undefined,
    time_slots_enabled: parameters.time_slots_enabled,
    time_slots: parameters.time_slots,
  };
  if (granularity !== undefined) {
    params.granularity = granularity;
  }
  return params;
}

export function dateRangeString(
  parameters:
    | Pick<CustomReportParameters, "starts_at" | "ends_at">
    | null
    | undefined
): string {
  const start = formatApiDate(parameters?.starts_at, "DD/MM/YYYY");
  const end = formatApiDate(parameters?.ends_at, "DD/MM/YYYY");
  return start && end ? `${start} - ${end}` : "";
}

const isAllToken = (v: FiltersListPermittedParams | undefined) =>
  Array.isArray(v) && v.includes("all");

const onlyNumbers = (v: FiltersListPermittedParams | undefined) =>
  Array.isArray(v) ? (v.filter((x) => typeof x === "number") as number[]) : [];

type TypedList = [string, FiltersListPermittedParams | undefined];

const productLists = (c: ReportTemplateCompetitor): TypedList[] => [
  ["company", c.companies],
  ["brand", c.brands],
  ["product", c.products],
];

const sectorLists = (c: ReportTemplateCompetitor): TypedList[] => [
  ["sector", c.sectors],
  ["category", c.categories],
  ["class", c.classes],
];

const allTokenTypes = (lists: TypedList[]): string[] =>
  lists.filter(([, list]) => isAllToken(list)).map(([type]) => type);

const namedEntries = (lists: TypedList[]) =>
  lists.flatMap(([type, list]) =>
    onlyNumbers(list).map((id) => ({ id, name: `#${id}`, type }))
  );

export function pendingSelectAllFromCompetitor(
  competitor: ReportTemplateCompetitor
): PendingSelectAll {
  return {
    broadcasters: isAllToken(competitor.competitors),
    agencies: isAllToken(competitor.agencies),
    productTypes: allTokenTypes(productLists(competitor)),
    sectorTypes: allTokenTypes(sectorLists(competitor)),
  };
}

export function parametersFromCompetitor(
  competitor: ReportTemplateCompetitor,
  parameters: CustomReportParameters
): CustomReportParameters {
  const broadcasters: MultiSelectBroadcaster[] = onlyNumbers(
    competitor.competitors
  ).map((id) => ({ id, name: `#${id}` }));
  const agencies: Agency[] = onlyNumbers(competitor.agencies).map((id) =>
    Object.assign(new Agency(), { id, name: `#${id}` })
  );
  const products = namedEntries(
    productLists(competitor)
  ) as unknown as ReportCompany[];
  const sectors = namedEntries(
    sectorLists(competitor)
  ) as unknown as ReportSector[];

  return {
    ...parameters,
    broadcasters,
    agencies,
    products,
    sectors,
    unit: competitor.type ?? parameters.unit,
    output_type: competitor.raw ? "raw" : "full",
    format_detail: competitor.formatDetail ?? false,
    granularity_enabled: competitor.temporalDetail ?? false,
    granularity: competitor.granularity ?? undefined,
    quotas: competitor.quota ?? false,
    inclusion: competitor.inclusion ?? false,
    special_initiatives_detail: competitor.showSpecialInitiatives ?? false,
    show_vod_devices: competitor.showVodDevices ?? false,
    apply_client_database: competitor.applyClientDatabase ?? false,
    attach_sector_category_class: competitor.attachSectorCategoryClass ?? false,
    auditel_target: competitor.grpTarget?.id ? competitor.grpTarget : null,
  };
}
